/**
 * Client for push notifications (OneSignal) — `/push`. Configure the OneSignal
 * App ID + REST API key, send manual broadcasts, and read the send history.
 *
 * Nothing here auto-pushes. There was an "auto-notify on these events" setting
 * and it broadcast business events to a whole segment — see OneSignalConfig.
 */
import type {
  CustomerPushStatus, OneSignalConfig, OneSignalConfigUpdate, PushAudiencePreview, PushNotificationLog,
  PushSendInput, PushSendResult,
} from '@erp/shared';

import { api } from './api.js';

export type { OneSignalConfig, PushNotificationLog } from '@erp/shared';

export async function getPushConfig(): Promise<OneSignalConfig> {
  return (await api().get<OneSignalConfig>('/push/config')).data;
}

export async function updatePushConfig(input: OneSignalConfigUpdate): Promise<OneSignalConfig> {
  return (await api().patch<OneSignalConfig>('/push/config', input)).data;
}

export async function sendPush(input: PushSendInput): Promise<PushSendResult> {
  return (await api().post<PushSendResult>('/push/send', input)).data;
}

export async function sendPushTest(): Promise<PushSendResult> {
  return (await api().post<PushSendResult>('/push/test', {})).data;
}

/** Can this one customer receive a device push right now? Read live from OneSignal. */
export async function getCustomerPushStatus(partyId: string): Promise<CustomerPushStatus> {
  return (await api().get<CustomerPushStatus>('/push/customer-status', { params: { partyId } })).data;
}

export async function listPushHistory(): Promise<PushNotificationLog[]> {
  return (await api().get<{ items: PushNotificationLog[] }>('/push/history')).data.items;
}

/** Who the chosen audience would actually reach — asked before sending. */
export async function getPushAudience(
  target: { partyId?: string; priceListId?: string },
): Promise<PushAudiencePreview> {
  return (await api().get<PushAudiencePreview>('/push/audience', { params: target })).data;
}
